import React from "react";
import { Helmet } from "react-helmet";
import { MapPin, Shield, Clock, BadgePoundSterling, Phone, Mail, MessageCircle } from "lucide-react";

const Navigation = () => {
  return (
    <nav className="dark-header">
      <a href="/">
        <img src="/logos/logo-v5.jpg" alt="ARCANA" className="dark-logo-nav" />
      </a>
      <div className="dark-nav">
        <a href="/" className="dark-nav-link">HOME</a>
        <a href="/#about" className="dark-nav-link">ABOUT</a>
        <a href="/services" className="dark-nav-link">SERVICES</a>
        <a href="/crypto-recovery-service-uk" className="dark-nav-link active">UK RECOVERY</a>
        <a href="/#pricing" className="dark-nav-link">PRICING</a>
        <a href="/faq" className="dark-nav-link">FAQ</a>
        <button className="btn-primary" onClick={() => window.location.href = '/#assessment'}>
          FREE ASSESSMENT
        </button>
      </div>
      <button className="mobile-menu-btn">
        <span style={{color: 'var(--oro-arcana)'}}>☰</span>
      </button>
    </nav>
  );
};

const CryptoRecoveryUKPage = () => {
  return (
    <div className="App">
      <Helmet>
        <title>Crypto Recovery Service UK | Non-Custodial Wallet Recovery</title>
        <meta name="description" content="UK-registered crypto recovery service. Seed phrase, password, derivation path recovery. Non-custodial, GDPR compliant. Free assessment." />
        <link rel="canonical" href="https://arcana-crypto.com/uk-recovery-seo.html" />
      </Helmet>
      <Navigation />
      
      <section className="section" style={{marginTop: '128px', minHeight: '100vh'}}>
        <div className="section-container">
          <h1 className="section-title" style={{fontSize: '48px', marginBottom: '24px'}}>
            Crypto Recovery Service UK (Non-Custodial)
          </h1>
          <p className="section-subtitle" style={{fontSize: '24px'}}>
            Professional Cryptocurrency Wallet Recovery Services Based in England & Wales
          </p>

          {/* Hero Introduction */}
          <div className="section-intro" style={{marginTop: '40px'}}>
            <p style={{fontSize: '20px', lineHeight: '1.8'}}>
              Arcana Crypto LTD is a UK-registered professional consultancy specializing in cryptocurrency 
              wallet recovery, security audits, and forensic analysis. Operating from England and Wales, 
              we provide non-custodial technical services to clients worldwide.
            </p>
          </div>

          {/* UK Legal Compliance */}
          <div className="legal-section" style={{marginTop: '60px'}}>
            <div className="category-header">
              <Shield className="category-icon" size={56} />
              <div>
                <h2 className="category-title">UK Legal Compliance & GDPR</h2>
                <p className="category-subtitle">
                  Registered and compliant with UK law
                </p>
              </div>
            </div>
            <div className="category-content" style={{paddingLeft: '0'}}>
              <ul className="column-list">
                <li>Company registered in England and Wales</li>
                <li>Full GDPR compliance for UK and EU clients</li>
                <li>Data processed locally and securely (no cloud storage)</li>
                <li>All communications encrypted (ProtonMail, Signal, WhatsApp, Telegram)</li>
                <li>Non-custodial operations - clients retain full fund control</li>
                <li>Professional Indemnity Insurance available upon request</li>
                <li>Strict adherence to UK financial services regulations</li>
              </ul>
            </div>
          </div>

          {/* Services for UK Clients */}
          <div style={{marginTop: '80px'}}>
            <h2 className="section-title">Services for UK Clients</h2>
            <p className="section-subtitle">Transparent Pricing in GBP (£)</p>

            <div className="card-grid" style={{marginTop: '60px'}}>
              <div className="card">
                <h3 className="card-title" style={{color: 'var(--oro-arcana)'}}>SEED PHRASE RECOVERY</h3>
                <p className="card-description">
                  Missing words, incorrect order, typos in BIP39 seed phrases
                </p>
                <ul className="card-list">
                  <li>1-2 missing words: £300-600</li>
                  <li>3 missing words: £1,200</li>
                  <li>4 missing words: £3,000</li>
                  <li>5 missing words: £2,000 + success fee</li>
                </ul>
                <div className="card-price">Success Rate: 70-95%</div>
                <a href="/#pricing-complete" className="card-link">
                  VIEW PRICING
                </a>
              </div>

              <div className="card">
                <h3 className="card-title" style={{color: 'var(--oro-arcana)'}}>WALLET PASSWORD RECOVERY</h3>
                <p className="card-description">
                  Encrypted wallet.dat, keystore files, hardware wallet PINs
                </p>
                <ul className="card-list">
                  <li>Owner-authorized CPU recovery: £600</li>
                  <li>GPU-accelerated recovery: £1,200</li>
                  <li>Pattern-based recovery available</li>
                  <li>Dictionary attacks with custom wordlists</li>
                </ul>
                <div className="card-price">Time: Hours to Days</div>
                <a href="/#pricing-complete" className="card-link">
                  VIEW PRICING
                </a>
              </div>

              <div className="card">
                <h3 className="card-title" style={{color: 'var(--oro-arcana)'}}>EMERGENCY SERVICES</h3>
                <p className="card-description">
                  Compromised wallets, scam victims, urgent migration
                </p>
                <ul className="card-list">
                  <li>Emergency wallet migration: £500 (30 min)</li>
                  <li>Deep wallet scan: £300-800</li>
                  <li>Security audit: £200-500</li>
                  <li>24/7 assessment available</li>
                </ul>
                <div className="card-price">Response: 30 Minutes</div>
                <a href="/#assessment" className="card-link">
                  GET HELP NOW
                </a>
              </div>

              <div className="card">
                <h3 className="card-title" style={{color: 'var(--oro-arcana)'}}>FORENSIC & SECURITY</h3>
                <p className="card-description">
                  Blockchain analysis, legal reports, security audits
                </p>
                <ul className="card-list">
                  <li>Forensic reports: £300-1,000</li>
                  <li>Law enforcement ready documentation</li>
                  <li>Security vulnerability assessment</li>
                  <li>Expert witness services available</li>
                </ul>
                <div className="card-price">Professional Reports</div>
                <a href="/services" className="card-link">
                  VIEW SERVICES
                </a>
              </div>
            </div>
          </div>

          {/* Why Choose UK-Based Service */}
          <div style={{marginTop: '100px'}}>
            <h2 className="section-title">Why Choose a UK-Based Recovery Service?</h2>
            
            <div className="two-column" style={{marginTop: '60px'}}>
              <div className="about-card">
                <h3 className="column-title">UK ADVANTAGES</h3>
                <ul className="column-list">
                  <li>Operating within established UK legal framework</li>
                  <li>Full GDPR compliance for data protection</li>
                  <li>Professional indemnity insurance available</li>
                  <li>Regulated business environment</li>
                  <li>English law contract protection</li>
                  <li>Transparent pricing in GBP (no FX fees)</li>
                </ul>
              </div>

              <div className="about-card">
                <h3 className="column-title">GLOBAL REACH</h3>
                <ul className="column-list">
                  <li>UK-based operations, worldwide service delivery</li>
                  <li>Remote service delivery available worldwide</li>
                  <li>Multilingual support (English, Italian, Spanish)</li>
                  <li>Timezone coverage for emergency cases</li>
                  <li>Remote service delivery (no office visit required)</li>
                  <li>International payment methods accepted</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Response Times */}
          <div style={{marginTop: '80px'}}>
            <h2 className="section-title">Response Times for UK Clients</h2>
            
            <div className="steps-grid" style={{marginTop: '60px'}}>
              <div className="step">
                <Clock className="step-icon" size={64} />
                <h3 className="step-title">STANDARD ASSESSMENT</h3>
                <p className="step-description">
                  Free technical feasibility evaluation
                </p>
                <div className="step-cost" style={{color: 'var(--oro-arcana)', fontSize: '24px'}}>
                  24-48 Hours
                </div>
              </div>

              <div className="step">
                <BadgePoundSterling className="step-icon" size={64} />
                <h3 className="step-title">EMERGENCY RESPONSE</h3>
                <p className="step-description">
                  Priority handling for compromised wallets
                </p>
                <div className="step-cost" style={{color: 'var(--oro-arcana)', fontSize: '24px'}}>
                  30 Minutes
                </div>
              </div>

              <div className="step">
                <MapPin className="step-icon" size={64} />
                <h3 className="step-title">UK BUSINESS HOURS</h3>
                <p className="step-description">
                  Monday-Sunday, 9:00-21:00 GMT
                </p>
                <div className="step-cost" style={{color: 'var(--oro-arcana)', fontSize: '24px'}}>
                  Same Day
                </div>
              </div>
            </div>
          </div>

          {/* Payment Methods UK */}
          <div style={{marginTop: '80px'}}>
            <h2 className="section-title">Payment Methods (UK Clients)</h2>
            
            <div className="pricing-notes" style={{marginTop: '40px'}}>
              <h3 style={{color: 'var(--oro-arcana)', marginBottom: '20px'}}>Accepted Payment Methods</h3>
              <ul className="column-list">
                <li>UK Bank Transfer (Faster Payments - instant)</li>
                <li>International Wire Transfer (SWIFT)</li>
                <li>Cryptocurrency (BTC, ETH, LTC, DOGE)</li>
                <li>Escrow services available for high-value cases</li>
                <li>Payment plans available for complex recoveries (£2,000+)</li>
              </ul>
            </div>
          </div>

          {/* UK-Specific FAQ */}
          <div style={{marginTop: '100px'}}>
            <h2 className="section-title">UK Client FAQ</h2>
            
            <div className="faq-accordion" style={{marginTop: '60px'}}>
              <div className="faq-accordion-item active">
                <div style={{padding: '24px 32px'}}>
                  <h3 className="faq-accordion-question">Is Arcana Crypto registered in the UK?</h3>
                  <div className="faq-accordion-content">
                    <p className="faq-answer">
                      Yes. Arcana Crypto LTD is registered in England and Wales. We operate under UK law 
                      and comply with all applicable regulations including GDPR, data protection, and 
                      consumer protection laws.
                    </p>
                  </div>
                </div>
              </div>

              <div className="faq-accordion-item active">
                <div style={{padding: '24px 32px'}}>
                  <h3 className="faq-accordion-question">Do I need to visit your office in the UK?</h3>
                  <div className="faq-accordion-content">
                    <p className="faq-answer">
                      No. All services are delivered remotely via encrypted communications (email, 
                      WhatsApp, Telegram, Signal). We serve UK clients from London to Edinburgh, 
                      Cardiff to Belfast - all from the comfort of your home.
                    </p>
                  </div>
                </div>
              </div>

              <div className="faq-accordion-item active">
                <div style={{padding: '24px 32px'}}>
                  <h3 className="faq-accordion-question">What cryptocurrencies do you support for UK clients?</h3>
                  <div className="faq-accordion-content">
                    <p className="faq-answer">
                      We support Bitcoin (BTC), Ethereum (ETH), Litecoin (LTC), and Dogecoin (DOGE) 
                      for all UK-based clients. All wallets following BIP39/BIP44 standards are supported.
                    </p>
                  </div>
                </div>
              </div>

              <div className="faq-accordion-item active">
                <div style={{padding: '24px 32px'}}>
                  <h3 className="faq-accordion-question">How long does recovery take for UK clients?</h3>
                  <div className="faq-accordion-content">
                    <p className="faq-answer">
                      Standard recoveries (1-2 missing words): Minutes to hours. Advanced recoveries 
                      (3-4 missing words): Hours to days. Emergency services: 30-minute response time. 
                      All UK clients receive priority assessment within 24 hours.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Contact CTA */}
          <div className="cta-section" style={{marginTop: '100px'}}>
            <h2 className="cta-title">UK-Based Crypto Recovery Experts</h2>
            <p className="cta-subtitle">
              Professional, confidential, non-custodial services for UK residents
            </p>
            
            <div style={{display: 'flex', gap: '24px', justifyContent: 'center', marginTop: '40px', flexWrap: 'wrap'}}>
              <a href="mailto:legal@arcana-crypto.com" className="btn-primary">
                <Mail size={20} />
                EMAIL US
              </a>
              <a 
                href="https://wa.me/447835822143?text=Hello%20Arcana%20Crypto,%20I%20need%20help%20with%20crypto%20recovery%20in%20the%20UK"
                target="_blank"
                rel="noopener noreferrer" 
                className="btn-primary"
              >
                <MessageCircle size={20} />
                WHATSAPP
              </a>
              <a 
                href="https://t.me/Arcana_Crypto_Lab"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary"
              >
                <Phone size={20} />
                TELEGRAM
              </a>
            </div>
            
            <p className="hero-small-text" style={{marginTop: '32px'}}>
              Free case assessment • No obligation • Confidential • Available 24/7
            </p>
          </div>

          {/* Trust Signals UK */}
          <div style={{marginTop: '80px', background: 'var(--nero-grigio)', padding: '48px', border: '1px solid var(--oro-arcana)'}}>
            <h3 style={{fontSize: '24px', color: 'var(--oro-arcana)', textAlign: 'center', marginBottom: '32px'}}>
              Our Credentials
            </h3>
            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '32px'}}>
              <div style={{textAlign: 'center'}}>
                <div style={{fontSize: '28px', fontWeight: '700', color: 'var(--oro-arcana)'}}>UK Registered</div>
                <div style={{fontSize: '14px', color: 'var(--grigio-chiaro)'}}>Company No. 16371124</div>
              </div>
              <div style={{textAlign: 'center'}}>
                <div style={{fontSize: '28px', fontWeight: '700', color: 'var(--oro-arcana)'}}>Hiscox Insured</div>
                <div style={{fontSize: '14px', color: 'var(--grigio-chiaro)'}}>PI £1M + Cyber £100K</div>
              </div>
              <div style={{textAlign: 'center'}}>
                <div style={{fontSize: '28px', fontWeight: '700', color: 'var(--oro-arcana)'}}>24/7</div>
                <div style={{fontSize: '14px', color: 'var(--grigio-chiaro)'}}>Emergency Response</div>
              </div>
            </div>
          </div>

        </div>
      </section>

      <footer className="footer">
        <div className="footer-content">
          <div className="footer-bottom">
            <p className="footer-copyright">
              © 2025-2026 ARCANA CRYPTO LTD - All Rights Reserved | Registered in England and Wales
            </p>
            <p style={{fontSize: '13px', color: 'var(--grigio-medio)', marginTop: '12px'}}>
              <a href="mailto:legal@arcana-crypto.com" className="footer-email">
                legal@arcana-crypto.com
              </a>
              {' | '}
              <a href="https://wa.me/447835822143" className="footer-email" target="_blank" rel="noopener noreferrer">
                WhatsApp
              </a>
              {' | '}
              <a href="https://t.me/Arcana_Crypto_Lab" className="footer-email" target="_blank" rel="noopener noreferrer">
                Telegram
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CryptoRecoveryUKPage;
